import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Wallet,
  Search,
  Users,
  CircleDollarSign,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';

import { useAuth, API } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';

const STATUS_LABELS = {
  open: { text: 'Pendiente', variant: 'destructive' },
  partial: { text: 'Parcial', variant: 'secondary' },
  paid: { text: 'Pagada', variant: 'outline' },
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const AccountsReceivableTab = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Filtros (client-side sobre el set completo)
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');

  // Diálogo de registro de pago
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedAR, setSelectedAR] = useState(null);
  const [payData, setPayData] = useState({ amount: 0, payment_method: 'cash', note: '' });

  // Carga el set COMPLETO (status=all) para poder calcular el resumen y filtrar localmente.
  const loadReceivables = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/accounts-receivable?status=all`, {
        headers: getAuthHeaders()
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar cuentas por cobrar'));
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    loadReceivables();
  }, [loadReceivables]);

  // Resumen
  const pendingRows = rows.filter((r) => r.status === 'open' || r.status === 'partial');
  const totalPending = pendingRows.reduce((acc, r) => acc + (Number(r.balance) || 0), 0);
  const totalCollected = rows.reduce(
    (acc, r) => acc + Math.max((Number(r.amount) || 0) - (Number(r.balance) || 0), 0),
    0
  );
  const customersWithDebt = new Set(pendingRows.map((r) => r.customer_phone)).size;

  // Filtrado visible
  const visibleRows = rows.filter((r) => {
    if (statusFilter === 'pending' && !(r.status === 'open' || r.status === 'partial')) return false;
    if (statusFilter === 'paid' && r.status !== 'paid') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (r.customer_name || '').toLowerCase();
      const phone = (r.customer_phone || '').toLowerCase();
      if (!name.includes(q) && !phone.includes(q)) return false;
    }
    return true;
  });

  const openPayDialog = (ar) => {
    setSelectedAR(ar);
    setPayData({ amount: Number(ar.balance) || 0, payment_method: 'cash', note: '' });
    setShowPayDialog(true);
  };

  const handleRegisterPayment = async () => {
    const amount = Number(payData.amount || 0);
    if (amount <= 0) {
      toast.error('El monto debe ser mayor que 0');
      return;
    }
    if (amount > (Number(selectedAR.balance) || 0) + 0.01) {
      toast.error('El abono excede el saldo pendiente');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/accounts-receivable/${selectedAR.id}/payment`,
        { amount, payment_method: payData.payment_method, note: payData.note || null },
        { headers: getAuthHeaders() }
      );
      toast.success('Pago registrado');
      setShowPayDialog(false);
      setSelectedAR(null);
      loadReceivables();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar el pago'));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Wallet className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold">Cuentas por Cobrar</h2>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-100">
              <CircleDollarSign className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total por cobrar</p>
              <p className="text-xl font-bold">${formatAmount(totalPending)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cobrado</p>
              <p className="text-xl font-bold">${formatAmount(totalCollected)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-100">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Clientes con deuda</p>
              <p className="text-xl font-bold">{customersWithDebt}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="paid">Pagadas</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por cliente o teléfono"
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={loadReceivables} disabled={loading}>
          Actualizar
        </Button>
      </div>

      {/* Tabla */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Factura</TableHead>
              <TableHead>Orden</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Vence</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {loading ? 'Cargando...' : 'No hay cuentas por cobrar'}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((ar) => {
                const st = STATUS_LABELS[ar.status] || STATUS_LABELS.open;
                const isOpen = ar.status === 'open' || ar.status === 'partial';
                return (
                  <TableRow key={ar.id}>
                    <TableCell className="font-medium">{ar.customer_name || '—'}</TableCell>
                    <TableCell>{ar.customer_phone}</TableCell>
                    <TableCell>{ar.invoice_number || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {(ar.order_id || '').slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-right">${formatAmount(ar.amount)}</TableCell>
                    <TableCell className="text-right font-semibold">${formatAmount(ar.balance)}</TableCell>
                    <TableCell>{formatDate(ar.due_date)}</TableCell>
                    <TableCell>
                      <Badge variant={st.variant}>{st.text}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isOpen ? (
                        <Button size="sm" onClick={() => openPayDialog(ar)}>
                          Registrar pago
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Liquidada</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Diálogo Registrar pago */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar pago</DialogTitle>
            <DialogDescription>
              {selectedAR
                ? `${selectedAR.customer_name || ''} ${selectedAR.customer_phone} · Saldo pendiente: $${formatAmount(selectedAR.balance)}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Monto</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={payData.amount}
                onChange={(e) => setPayData({ ...payData, amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Método</Label>
              <Select
                value={payData.payment_method}
                onValueChange={(v) => setPayData({ ...payData, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Método de pago" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Input
                value={payData.note}
                onChange={(e) => setPayData({ ...payData, note: e.target.value })}
                placeholder="Referencia, observación..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRegisterPayment} disabled={loading}>
              {loading ? 'Registrando...' : 'Registrar pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsReceivableTab;
